import AppTitle from '@/components/AppTitle';
import MainPageTitle from '@/components/MainPageTitle';
import PushToRight from '@/components/PushToRight';
import Space from '@/components/Space';
import api from '@/lib/api';
import ApiError from '@/lib/api/ApiError';
import validateUrl from '@/lib/validation/validateUrl';
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import Card from '@/components/Card';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import useSwr from '@/lib/useSwr';
import { type School } from '../schools/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import NoneMenuItem from '../Audit/components/SearchForm/NoneMenuItem';

type DataMigrationErrors = {
  schoolId: string | null;
  legacyDatabaseUrl: string | null;
  legacyDatabaseServiceAccount: string | null;
};

const StyledSelect = styled(Select)({
  minWidth: 240,
  maxWidth: 240,
}) as unknown as typeof Select;

const CreateDataMigration = () => {
  const { isLoading, data: schools, error } = useSwr<School[]>('/schools');

  const [schoolId, setSchoolId] = useState<number | ''>('');
  const [legacyDatabaseUrl, setLegacyDatabaseUrl] = useState('');
  const [legacyDatabaseServiceAccount, setLegacyDatabaseServiceAccount] =
    useState('');

  const [dataMigrationErrors, setDataMigrationErrors] =
    useState<DataMigrationErrors>({
      schoolId: null,
      legacyDatabaseUrl: null,
      legacyDatabaseServiceAccount: null,
    });

  const [saving, setSaving] = useState(false);
  const [genericSaveError, setGenericSaveError] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleSave = async (): Promise<void> => {
    setGenericSaveError(null);

    const dataMigrationErrors = {
      schoolId: schoolId === '' ? 'Please select a school' : null,
      legacyDatabaseUrl: validateUrl(legacyDatabaseUrl),
      legacyDatabaseServiceAccount:
        legacyDatabaseServiceAccount.trim().length === 0
          ? 'Please enter a service account'
          : null,
    };

    setDataMigrationErrors(dataMigrationErrors);

    if (
      dataMigrationErrors.schoolId !== null ||
      dataMigrationErrors.legacyDatabaseUrl !== null ||
      dataMigrationErrors.legacyDatabaseServiceAccount !== null
    ) {
      return;
    }

    setSaving(true);

    try {
      await api.post('/data-migrations', {
        schoolId,
        legacyDatabaseUrl,
        legacyDatabaseServiceAccount,
      });

      navigate('/data-migrations');
    } catch (error) {
      if (!(error instanceof ApiError)) {
        throw error;
      }

      setGenericSaveError(error.getErrorCode());
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner margin={48} centered />;
  }

  if (error || !schools) {
    return (
      <Alert severity="error">
        Something went wrong while trying to load schools.
      </Alert>
    );
  }

  return (
    <>
      <AppTitle>New data migration</AppTitle>
      <MainPageTitle>New data migration</MainPageTitle>
      {genericSaveError !== null && (
        <>
          <Alert severity="error">
            Could not save school with error code: {genericSaveError}. Please
            try again later.
          </Alert>
          <Space height={24} />
        </>
      )}
      <Card>
        <Card.Title>Target school</Card.Title>
        <FormControl error={dataMigrationErrors.schoolId !== null}>
          <StyledSelect
            value={schoolId}
            onChange={(e: SelectChangeEvent<number>) =>
              setSchoolId(e.target.value as number | '')
            }
            size="small"
            displayEmpty
          >
            <NoneMenuItem value="">None</NoneMenuItem>
            {schools.map(school => (
              <MenuItem value={school.id} key={school.id}>
                {school.displayName}
              </MenuItem>
            ))}
          </StyledSelect>
          {dataMigrationErrors.schoolId !== null && (
            <FormHelperText>{dataMigrationErrors.schoolId}</FormHelperText>
          )}
        </FormControl>
      </Card>
      <Card>
        <Card.Title>Legacy database information</Card.Title>
        <Card.TextField
          label="Legacy database URL"
          name="legacy_database_url"
          value={legacyDatabaseUrl}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setLegacyDatabaseUrl(e.target.value)
          }
          error={dataMigrationErrors.legacyDatabaseUrl !== null}
          helperText={dataMigrationErrors.legacyDatabaseUrl}
          disabled={saving}
          width="50%"
        />
        <Space height={12} />
        <Card.TextField
          label="Legacy database service account"
          name="legacy_database_service_account"
          value={legacyDatabaseServiceAccount}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setLegacyDatabaseServiceAccount(e.target.value)
          }
          type="password"
          error={dataMigrationErrors.legacyDatabaseServiceAccount !== null}
          helperText={dataMigrationErrors.legacyDatabaseServiceAccount}
          disabled={saving}
          width="50%"
        />
      </Card>
      <PushToRight>
        <Button variant="contained" onClick={handleSave} loading={saving}>
          Create
        </Button>
      </PushToRight>
    </>
  );
};

export default CreateDataMigration;
